<template>
  <div class="text-editor__block">
    <div v-if="showTitle" class="block-title">Comment</div>
    <div class="d-flex" :ref="`comment-${entityId}`" v-if="inModal">
      <div class="comment-container" @click="showEditor = true" style="display: flex; width: 100%;">
        <span class="material-symbols-outlined" :class="{ 'has-comment': editorData, 'no-comment': !showText && emptyText && !formattedText }">
          comment
        </span>
        <template v-if="showText">
          <div class="d-inline-block ml-1 column-comment"  style="flex: 1;" :class="{ 'no-comment': emptyText && !formattedText }"
            v-html="formattedText || emptyText" />
        </template>
      </div>
      <b-modal :visible="isEditorMode" ignore-enforce-focus-selector=".ck-balloon-panel" centered :modal-class="isLoading ? 'loading' : ''">
        <template v-slot:modal-header="{ close }">
          <h5>Change comment for {{ entityType }}</h5>

          <button type="button" aria-label="Close" class="close" @click="checkClose()">
            <span class="material-icons">
              close
            </span>
          </button>
        </template>

        <div class="modal-fill">
          <ckeditor :editor="editor" v-model="editorData" :config="editorConfig" @ready="onEditorReady" />
        </div>

        <template v-slot:modal-footer>
          <b-button variant="outline" class="button" :disabled="isLoading" @click="checkClose()">
            Cancel
          </b-button>
          <b-button variant="primary" class="button" :disabled="isLoading" @click="saveText()">
            <b-spinner v-if="isLoading" small class="mr-2"></b-spinner>
            {{isLoading ? 'LOADING...' : 'Save'}}
          </b-button>
        </template>
      </b-modal>
      <b-tooltip :target="()=>$refs[`comment-${entityId}`]" :delay="delayParams" variant="base" container="body" placement="top" boundary="viewport" :custom-class="isWideTooltip || editorData.length > 120 ? 'wide-tooltip' : ''" v-if="editorData && (isWideTooltip ? editorData.length > 120 : true)">
        <div v-html="editorData" v-if="editorData"></div>
        <div v-else>{{ emptyText }}</div>
      </b-tooltip>
    </div>
    <template v-else>
      <template v-if="isEditorMode">
        <ckeditor :editor="editor" v-model="editorData" :config="editorConfig" />

        <div class="d-flex justify-content-end my-2 ">
          <b-button variant="outline" class="button" @click="checkClose()">
            Cancel
          </b-button>
          <b-button variant="primary" class="button ml-2" :disabled="isLoading" @click="saveText()">
            <b-spinner v-if="isLoading" small class="mr-2"></b-spinner>
            Save
          </b-button>
        </div>
      </template>
      <div class="d-flex justify-content-between" v-else>
        <p v-html="commentText || emptyText" />
        <span class="material-icons text-editor__block-icon ml-2" @click="showEditor = true">edit</span>
      </div>
    </template>
  </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { mapGetters, mapActions } from 'vuex'
import { permissions } from '@mocks/permissions'
export default {
  name: 'TextEditor',
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    showText: {
      type: Boolean,
      default: false
    },
    dataAlreadyLoaded: {
      type: Boolean,
      default: false
    },
    inModal: {
      type: Boolean,
      default: true
    },
    entityType: {
      type: String,
      default: null
    },
    entityId: {
      type: [ String, Number ]
    },
    emptyText: {
      type: String,
      default: 'No comment'
    },
    showTitle: {
      type: Boolean,
      default: false
    },
    isWideTooltip: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      showEditor: false,
      editor: ClassicEditor,
      commentText: '',
      editorData: '',
      editorConfig: {
        toolbar: [ 'heading', '|', 'bold', 'italic', 'strike', 'link', '|', 'NumberedList', 'BulletedList', 'blockQuote', '|', 'alignment', '|', 'undo', 'redo' ],
        dialog: {
          definition: {
            textInput: {
              maxLength: 10
            }
          }
        }
      },
      delayParams: {
        show: 1000,
        hide: 100
      },
      formattedText: ''
    }
  },
  computed: {
    ...mapGetters({
      getComment: 'comments/getComment'
    }),
    isEditorMode () {
      return this.$can(permissions.UpdateAllComments) && this.showEditor
    }
  },
  watch: {
    showModal (e) {
      this.showEditor = e
    },
    entityId (entityId) {
      this.setDataByEntityId(entityId)
    }
  },
  async mounted () {
    if (!this.dataAlreadyLoaded && this.entityType && this.$can(permissions.ReadAllComments)) {
      await this.fetchComments({ type: this.entityType, id: this.entityId })
    }
    this.setDataByEntityId(this.entityId)
  },
  methods: {
    ...mapActions({
      setComment: 'comments/setComment',
      fetchComments: 'comments/fetchComments'
    }),
    setDataByEntityId (entityId) {
      this.commentText = this.getComment(this.entityType, entityId).comment ?? ''
      this.editorData = this.commentText
      this.formattedText = this.getEditData(this.editorData)
    },
    async checkClose () {
      if (this.commentText !== this.editorData) {
        const confirm = await this.$root.$confirm.open(
          'Close Modal',
          'Are you sure you don\'t want to keep the changes?',
          {
            submitText: 'Discard'
          }
        )
        if (confirm) {
          this.editorData = this.commentText
          this.showEditor = false
          this.$emit('close-editor')
        }
      } else {
        this.showEditor = false
      }
    },
    async saveText () {
      this.isLoading = true
      this.commentText = this.editorData
      await this.setComment({ type: this.entityType, id: this.entityId, comment: this.editorData })
      this.formattedText = this.getEditData(this.editorData)
      this.$emit('saveText', { data: this.formattedText, entityId: this.entityId })
      this.isLoading = false
      this.showEditor = false
    },
    getEditData (comment) {
      if (!comment) {
        return ''
      }
      const regex = /( |<([^>]+)>)/ig
      const result = comment.replace(regex, ' ')

      return result
    },
    onEditorReady (editor) {
      editor.model.change(writer => {
        writer.setSelection(editor.model.document.getRoot(), 'end')
      })
      editor.editing.view.focus()
      editor.editing.view.document.on('keydown', (evt, data) => {
        if (data.keyCode === 13 && (data.shiftKey || data.metaKey)) {
          data.preventDefault()
          evt.stop()
          this.saveText()
        }
      }, { priority: 'highest' })
    }
  }
}
</script>

<style lang="scss" scoped>

  .comment-container {
    cursor: pointer;
    align-items: center;
    display: flex;
  }

  .ck.ck-balloon-panel:not(.ck-toolbar-container) {
    z-index: 1080;
  }
</style>
